import { useCallback, useEffect, useMemo, useState } from 'react'

import { useUpdateCheckpoint } from '../hooks/useUpdateCheckpoint'
import { useIsEditModeSatisfied } from '@/hooks/useIsEditModeSatisfied'
import { usePermissions } from '@/hooks/usePermissions'
import { useTranslation } from 'react-i18next'

import { Button, Box as DarkBox, TextInput } from '@hivewatch/dark'

import { CheckpointIProps } from '@/features/floor-plan'

import { DarkFloorPlanSidePanel } from '../../dark/DarkFloorPlanSidePanel'
import { CheckpointTaskInputI } from '../types/types'
import { hasChecklistUpdated, hasDuplicateChecklistItems } from '../utils/utils'
import { DarkCheckPointQRCode } from './DarkCheckPointQRCode'
import { darkCheckPointsPanelStyles } from './DarkCheckPointsPanel.styles'
import { DarkCheckpointChecklist } from './DarkCheckpointChecklist'
import { DarkEditCheckPointHeader } from './DarkEditCheckPointHeader'
import { DarkVerificationPhotoCheckbox } from './DarkVerificationPhotoCheckbox'

interface DarkEditCheckPointIProps {
  checkpoint: CheckpointIProps
  colorScheme: 'dark' | 'light'
  isExpanded: boolean
  onToggleExpansion: () => void
  onDone: () => void
  onClose: () => void
}

// Dark twin of EditCheckPoint. The form chrome (header, inputs, footer) is
// rebuilt on dark components; the QR code and checklist clusters are reused
// as-is until their own rebrand pass.
export const DarkEditCheckPoint = ({
  checkpoint,
  colorScheme,
  isExpanded,
  onToggleExpansion,
  onClose,
  onDone,
}: DarkEditCheckPointIProps) => {
  const { t } = useTranslation()
  const { shouldEnableUpdateCheckpoint, shouldEnableCheckpointChecklist } =
    usePermissions()
  const isEditMode = useIsEditModeSatisfied()

  const [name, setName] = useState(checkpoint?.name || '')
  const [checklistItems, setChecklistItems] = useState<CheckpointTaskInputI[]>(
    []
  )
  const [hasChecklistChanges, setHasChecklistChanges] = useState(false)

  const {
    areTasksRequired,
    areCheckpointTasksLoading,
    checkpointTasks,
    existingChecklistValues,
    hasRequiredChanged,
    hasPhotoRequiredChanged,
    isLoading,
    isPhotoRequired,
    setAreTasksRequired,
    handleIsPhotoRequired,
    updateCheckpoint,
    isGuardToursV2,
  } = useUpdateCheckpoint({
    checkpointId: checkpoint?.id,
    isPhotoRequiredDefault: checkpoint?.isPhotoRequired,
    onDone,
  })

  const hasDuplicates = useMemo(
    () => hasDuplicateChecklistItems(checklistItems),
    [checklistItems]
  )

  const isNameValid = /\S/.test(name)
  const hasValidInputs = isNameValid && !hasDuplicates
  const hasNameChanged = checkpoint?.name !== name
  const hasChanges =
    hasNameChanged ||
    hasChecklistChanges ||
    hasRequiredChanged ||
    hasPhotoRequiredChanged

  const handleChecklistUpdate = useCallback(
    (formattedItems: CheckpointTaskInputI[]) => {
      setChecklistItems(formattedItems)
      setHasChecklistChanges(
        hasChecklistUpdated(formattedItems, checkpointTasks)
      )
    },
    [checkpointTasks]
  )

  useEffect(() => {
    setName(checkpoint?.name || '')
  }, [checkpoint])

  // Leaving edit mode discards in-progress name edits (legacy parity).
  useEffect(() => {
    if (!isEditMode) {
      setName(checkpoint?.name || '')
    }
  }, [isEditMode, checkpoint?.name])

  const onSubmit = async () => {
    await updateCheckpoint(
      checkpoint.id,
      name,
      areTasksRequired,
      checklistItems
    )
  }

  const s = darkCheckPointsPanelStyles(colorScheme)

  const footer = isEditMode ? (
    <DarkBox {...s.editFooter}>
      <Button colorScheme={colorScheme} onClick={onClose} variant='tertiary'>
        {t('common:cancel')}
      </Button>
      <Button
        colorScheme={colorScheme}
        data-testid='DarkEditCheckPoint:save'
        disabled={
          !hasValidInputs ||
          !hasChanges ||
          !shouldEnableUpdateCheckpoint ||
          (hasChecklistChanges && !shouldEnableCheckpointChecklist)
        }
        isLoading={isLoading}
        onClick={onSubmit}
        variant='primary'
      >
        {t('floors:checkpoint_save_changes_button')}
      </Button>
    </DarkBox>
  ) : undefined

  return (
    <DarkFloorPlanSidePanel
      dataTestid='floorPlanPage_darkCheckPointsPanel'
      footer={footer}
      header={
        <DarkEditCheckPointHeader
          colorScheme={colorScheme}
          isEditMode={isEditMode}
          onClose={onClose}
        />
      }
      isExpanded={isExpanded}
      onToggleExpansion={onToggleExpansion}
    >
      <DarkBox {...s.editForm}>
        <TextInput
          colorScheme={colorScheme}
          data-testid='DarkEditCheckPoint:name'
          disabled={!isEditMode}
          hasWarning={!isNameValid}
          label={t('floors:checkpoint_edit_checkpoint_name_label')}
          onValueChange={setName}
          value={name}
          warningText={t('floors:checkpoint_edit_name_error')}
          width='100%'
        />
        <TextInput
          colorScheme={colorScheme}
          data-testid='DarkEditCheckPoint:id'
          disabled
          label={t('floors:checkpoint_edit_checkpoint_id_label')}
          onValueChange={() => undefined}
          value={checkpoint?.displayId || ''}
          width='100%'
        />
        <TextInput
          colorScheme={colorScheme}
          data-testid='DarkEditCheckPoint:facility'
          disabled
          label={t('floors:checkpoint_facility_label')}
          onValueChange={() => undefined}
          value={checkpoint?.facility?.name || ''}
          width='100%'
        />
        <TextInput
          colorScheme={colorScheme}
          data-testid='DarkEditCheckPoint:floor'
          disabled
          label={t('floors:checkpoint_floor_label')}
          onValueChange={() => undefined}
          value={checkpoint?.floor?.name || ''}
          width='100%'
        />
        <DarkBox {...s.editSectionLabel}>
          {t('floors:checkpoint_edit_qr_code_label')}
        </DarkBox>
        <DarkCheckPointQRCode
          colorScheme={colorScheme}
          displayId={checkpoint?.displayId}
          id={checkpoint?.id}
          name={checkpoint?.name}
        />
        {isGuardToursV2 && (
          <DarkBox>
            {areCheckpointTasksLoading ? (
              shouldEnableCheckpointChecklist && (
                <DarkBox {...s.emptySection} py='16px'>
                  {t('floors:checkpoint_checklist_loading')}
                </DarkBox>
              )
            ) : (
              <DarkCheckpointChecklist
                areTasksRequired={areTasksRequired}
                colorScheme={colorScheme}
                existingValues={existingChecklistValues}
                hasDuplicateItems={hasDuplicates}
                isInEditMode={isEditMode}
                onRequiredChange={setAreTasksRequired}
                onUpdate={handleChecklistUpdate}
              />
            )}
            <DarkVerificationPhotoCheckbox
              colorScheme={colorScheme}
              isChecked={isPhotoRequired}
              isDisabled={!isEditMode || !shouldEnableCheckpointChecklist}
              onRequiredChange={handleIsPhotoRequired}
            />
          </DarkBox>
        )}
      </DarkBox>
    </DarkFloorPlanSidePanel>
  )
}
